import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CalendarClock, Megaphone, Send, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { useConfig } from "@/context/ConfigContext";
import { EmptyState, Loader, PageTitle, StatusBadge } from "@/components/common";
import { DateField } from "@/components/DateField";

const STATUS = { programmee: ["Programmée", "warning"], envoi: ["Envoi en cours", "info"], envoyee: ["Envoyée", "success"], annulee: ["Annulée", "neutral"] };
const MEMBER_STATUSES = [["actif", "Membres actifs"], ["suspendu", "Membres suspendus"]];

function Chips({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map(([v, l]) => {
        const on = value.includes(v);
        return <button key={v} type="button" data-testid={`${testId}-${v}`} onClick={() => onChange(on ? value.filter((x) => x !== v) : [...value, v])}
          className={`px-3 h-8 rounded-full border text-xs transition-colors ${on ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2 hover:border-mr-text2"}`}>{l}</button>;
      })}
    </div>
  );
}

function Composer({ onSent }) {
  const { settings } = useConfig();
  const { state } = useLocation();
  const [picked, setPicked] = useState(state?.userIds ? { ids: state.userIds, names: state.names || [] } : null);
  const empty = { title: "", body: "", sectors: [], communes: [], statuses: ["actif"], push: true, email: true, when: "now", at: "" };
  const [f, setF] = useState(empty);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const audience = { sectors: f.sectors, communes: f.communes, statuses: picked ? ["actif", "suspendu"] : f.statuses, user_ids: picked ? picked.ids : [] };
  useEffect(() => {
    api.post("/admin/announcements/audience-count", audience).then(({ data }) => setCount(data.count)).catch(() => setCount(null));
  }, [f.sectors, f.communes, f.statuses, picked]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (f.when === "later" && !f.at) return toast.error("Choisissez la date et l'heure d'envoi.");
    if (!window.confirm(f.when === "now" ? `Envoyer cette annonce à ${count} membre(s) maintenant ?` : "Programmer cette annonce ?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/announcements", { title: f.title, body: f.body, audience, channels: { push: f.push, email: f.email },
        scheduled_at: f.when === "later" ? new Date(f.at).toISOString() : null });
      toast.success(data.status === "programmee" ? "Annonce programmée" : "Annonce en cours d'envoi");
      setF(empty);
      setPicked(null);
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const all = !picked && !f.sectors.length && !f.communes.length && f.statuses.includes("actif");
  return (
    <div className="mr-card space-y-5" data-testid="announcement-composer">
      <label className="block"><span className="mr-label">Titre</span>
        <input data-testid="announcement-title-input" className="mr-input" maxLength={140} value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex. Assemblée générale du 12 décembre" /></label>
      <label className="block"><span className="mr-label">Message</span>
        <textarea data-testid="announcement-body-input" rows={6} className="mr-input resize-y" maxLength={5000} value={f.body} onChange={(e) => set("body", e.target.value)} /></label>
      <div className="space-y-3">
        <p className="mr-label flex items-center gap-2"><Users size={14} /> Destinataires</p>
        {picked ? (
          <div className="mr-card p-3 text-sm flex items-start justify-between gap-3" data-testid="announcement-picked-members">
            <span>Membres sélectionnés ({picked.ids.length}) : {picked.names.slice(0, 8).join(", ")}{picked.names.length > 8 ? "…" : ""}</span>
            <button data-testid="announcement-picked-clear" className="mr-btn-ghost px-2" onClick={() => setPicked(null)}><X size={14} /></button>
          </div>) : <>
        <Chips testId="announcement-status" options={MEMBER_STATUSES} value={f.statuses} onChange={(v) => set("statuses", v.length ? v : ["actif"])} />
        <p className="text-xs text-mr-muted">Secteurs (aucun = tous)</p>
        <Chips testId="announcement-sector" options={(settings["catalog.sectors"] || []).map((s) => [s, s])} value={f.sectors} onChange={(v) => set("sectors", v)} />
        <p className="text-xs text-mr-muted">Communes (aucune = toutes)</p>
        <Chips testId="announcement-commune" options={(settings["catalog.cities"] || []).map((s) => [s, s])} value={f.communes} onChange={(v) => set("communes", v)} /></>}
        <p className="text-sm" data-testid="announcement-audience-count"><span className="font-semibold tabular">{count ?? "…"}</span> membre(s) recevront l'annonce{all ? ", publiée aussi dans le fil." : "."}</p>
      </div>
      <div className="space-y-2">
        <p className="mr-label">Canaux</p>
        <p className="text-xs text-mr-muted">Dans l'application : toujours.</p>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="announcement-push-checkbox" checked={f.push} onChange={(e) => set("push", e.target.checked)} /> Notification push</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="announcement-email-checkbox" checked={f.email} onChange={(e) => set("email", e.target.checked)} /> E-mail</label>
      </div>
      <div className="space-y-2">
        <p className="mr-label">Envoi</p>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="radio" name="when" data-testid="announcement-when-now" checked={f.when === "now"} onChange={() => set("when", "now")} /> Tout de suite</label>
          <label className="flex items-center gap-2"><input type="radio" name="when" data-testid="announcement-when-later" checked={f.when === "later"} onChange={() => set("when", "later")} /> À une date choisie</label>
        </div>
        {f.when === "later" && <><DateField withTime testId="announcement-datetime-input" className="max-w-md" value={f.at} onChange={(v) => set("at", v)} fromYear={new Date().getFullYear()} />
          <p className="text-xs text-mr-muted">L'envoi part dans le quart d'heure qui suit l'heure choisie.</p></>}
      </div>
      <button data-testid="announcement-submit-button" className="mr-btn-primary" disabled={busy || f.title.trim().length < 3 || f.body.trim().length < 3 || count === 0} onClick={submit}>
        {f.when === "now" ? <Send size={16} /> : <CalendarClock size={16} />} {busy ? "Envoi…" : f.when === "now" ? "Envoyer l'annonce" : "Programmer l'annonce"}
      </button>
    </div>
  );
}

function Reads({ id }) {
  const [list, setList] = useState(null);
  if (!list) return <button data-testid="announcement-reads-button" className="mr-btn-ghost -ml-3 text-xs" onClick={() => api.get(`/admin/announcements/${id}/reads`).then(({ data }) => setList(data))}>Voir qui l'a lue</button>;
  return (
    <ul className="text-xs grid sm:grid-cols-2 gap-1" data-testid="announcement-reads-list">{list.map((u) => (
      <li key={u.id} className={u.read ? "text-mr-accent" : "text-mr-muted"}>{u.read ? "Lue" : "Non lue"} · {u.name}</li>))}</ul>
  );
}

export default function ConsoleAnnouncements() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/announcements").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);
  const cancel = async (id) => {
    if (!window.confirm("Annuler cette annonce programmée ?")) return;
    try {
      await api.post(`/admin/announcements/${id}/cancel`);
      toast.success("Annonce annulée");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mr-fade-up" data-testid="console-announcements">
      <PageTitle eyebrow="Communauté" title="Annonces du club" />
      <div className="grid xl:grid-cols-[minmax(0,1fr)_420px] gap-6 items-start">
        <Composer onSent={() => setTimeout(load, 800)} />
        <section className="space-y-3">
          <h2 className="mr-eyebrow">Historique</h2>
          {items === null ? <Loader /> : items.length === 0 ? <EmptyState icon={Megaphone} title="Aucune annonce" text="Les annonces envoyées ou programmées apparaîtront ici." /> : items.map((a) => {
            const [label, tone] = STATUS[a.status] || [a.status, "neutral"];
            return (
              <div key={a.id} className="mr-card space-y-2" data-testid="announcement-row">
                <div className="flex items-start justify-between gap-3"><p className="font-medium">{a.title}</p><StatusBadge status={a.status} label={label} tone={tone} /></div>
                <p className="text-sm text-mr-text2 line-clamp-2">{a.body}</p>
                <p className="text-xs text-mr-muted tabular">
                  {a.status === "envoyee" ? `Envoyée le ${fdatetime(a.sent_at)} · ${a.recipients_count} destinataire(s) · lue par ${a.read_count || 0}` : a.status === "programmee" ? `Prévue le ${fdatetime(a.scheduled_at)}` : a.status === "annulee" && a.cancelled_at ? `Annulée le ${fdatetime(a.cancelled_at)}` : fdatetime(a.created_at)}
                  {" · "}{a.author_name}{a.channels?.push ? " · push" : ""}{a.channels?.email ? " · e-mail" : ""}
                </p>
                {a.status === "envoyee" && <Reads id={a.id} />}
                {a.status === "programmee" && <button data-testid="announcement-cancel-button" className="mr-btn-ghost text-mr-danger -ml-3" onClick={() => cancel(a.id)}><X size={14} /> Annuler</button>}
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}
